import { PlaidBank } from "types/Models/bankAccount";
import {
  Account,
  BankBalances,
  BankBrand,
  Banks,
  BankStatement,
  ErrorItem,
} from "types/Models/banks";
import { PlaidBankAccountMap } from "types/Models/plaid";
import { emptyApi } from "./emptyApi";
import qs from "qs";
import { AccountConnectionType } from "constants/dataSource";

type ExchangeTokenProps = {
  groupId: string;
  public_token: string;
  entity_id: string;
  institution_id: string;
  accounts: { name: string; mask: string }[];
};

export type AccountMigrateMap = {
  account_id?: string;
  name?: string;
  mask?: string;
  action?: "closed" | "keep_active";
  closed_on?: string;
  closing_balance?: number;
};

export type LedgerBanks = {
  accounts: Banks[];
  aggregate: {
    accounts: number;
    current_balance: number;
  };
  invoice_count?: number;
  bill_count?: number;
  customer_count?: number;
  vendor_count?: number;
  items: ErrorItem[];
};

export const bankConnectionsApi = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    getGenerateLinkToken: build.query<
      {
        expiration: string;
        link_token: string;
        request_id: string;
      },
      {
        groupId: string;
        entityId: string;
        entityItemId?: string;
        isReconnect?: boolean;
      }
    >({
      query: ({ groupId, entityItemId, entityId, isReconnect }) => {
        const queries = {
          entity_item_id: entityItemId || null,
          is_reconnect: isReconnect || null,
        };
        let queryUrl = qs.stringify(queries, {
          skipNulls: true,
          addQueryPrefix: true,
        });
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/link_token/${queryUrl}`,
        };
      },
    }),

    getPlaidBanks: build.query<PlaidBank[], { groupId: string }>({
      query: ({ groupId }) =>
        `/api/inkle/bookkeeping/group/${groupId}/plaid_bank_account/`,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ item_data: { uuid } }) => ({
                type: "PlaidBanks" as "PlaidBanks",
                id: uuid,
              })),
              { type: "PlaidBanks" as "PlaidBanks", id: "LIST" },
            ]
          : [{ type: "PlaidBanks" as "PlaidBanks", id: "LIST" }],
    }),

    exchangeToken: build.mutation<PlaidBankAccountMap, ExchangeTokenProps>({
      query: ({
        public_token,
        groupId,
        entity_id,
        institution_id,
        accounts,
      }) => ({
        url: `/api/inkle/bookkeeping/group/${groupId}/plaid_bank_account/`,
        method: "POST",
        body: {
          public_token,
          entity_id,
          institution_id,
          accounts,
        },
      }),
      invalidatesTags: (_result, _error) => {
        return [
          { type: "PlaidBanks", id: "LIST" },
          { type: "CONNECTION" },
          { type: "Subscriptions" },
        ];
      },
    }),

    syncPlaidTransactions: build.mutation({
      query: ({
        groupId,
        entity_item_id,
      }: {
        groupId: string;
        entity_item_id: string;
      }) => {
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/plaid_transactions/`,
          method: "post",
          body: {
            entity_item_id,
          },
        };
      },
    }),

    getPlaidBankLogo: build.query({
      query: ({
        groupId,
        institution_id,
      }: {
        groupId: string;
        institution_id: string;
      }) => ({
        url: `/api/inkle/bookkeeping/group/${groupId}/plaid_institutions/`,
        params: {
          ins_id: institution_id,
        },
      }),
    }),

    updateItemErrorState: build.mutation<
      void,
      { groupId: string; entityItemId: string; publicToken?: string }
    >({
      query: ({ entityItemId, groupId, publicToken }) => ({
        url: `/api/inkle/bookkeeping/group/${groupId}/item/${entityItemId}/update_error_state`,
        method: "put",
        body: {
          public_token: publicToken,
        },
      }),
      invalidatesTags: () => [
        { type: "PlaidBanks" as "PlaidBanks", id: "LIST" },
      ],
    }),

    getEntityBanks: build.query<
      LedgerBanks,
      { groupId: string; entityId: string; type?: AccountConnectionType }
    >({
      query: ({ groupId, entityId, type }) => {
        const queries = {
          type: type || null,
        };
        let queryUrl = qs.stringify(queries, {
          skipNulls: true,
          addQueryPrefix: true,
        });
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/ledger_banks/${queryUrl}`,
        };
      },
      providesTags: (result, error, arg) =>
        result
          ? [
              ...result.accounts.map(({ account: { uuid } }) => ({
                type: "PlaidBanks" as "PlaidBanks",
                id: uuid,
              })),
              { type: "PlaidBanks" as "PlaidBanks", id: "LIST" },
              {
                type: "PlaidBanks" as "PlaidBanks",
                id: `ENTITY_${arg.entityId}`,
              },
              { type: "CONNECTION" },
            ]
          : [
              { type: "PlaidBanks" as "PlaidBanks", id: "LIST" },
              {
                type: "PlaidBanks" as "PlaidBanks",
                id: `ENTITY_${arg.entityId}`,
              },
              { type: "CONNECTION" },
              { type: "Subscriptions" },
            ],
    }),

    editBankAccount: build.mutation<
      Banks,
      {
        groupId: string;
        entityId: string;
        bankId: string;
        bankAccountId: string;
        payload: {
          opening_balance?: string;
          opening_balance_date?: string;
          closed_on?: string;
          account_status?: Account["status"];
          closing_balance?: number;
          currency?: string;
        };
      }
    >({
      query: ({ groupId, entityId, bankId, bankAccountId, payload }) => ({
        url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/entity_item/${bankId}/bank_accounts/${bankAccountId}/`,
        body: payload,
        method: "PATCH",
      }),
      invalidatesTags: (_result, _error) => {
        return [{ type: "PlaidBanks", id: "LIST" }, { type: "Subscriptions" }];
      },
    }),

    syncEntityTransactions: build.mutation<
      { message: string }[],
      { groupId: string; entityId: string }
    >({
      query: ({ groupId, entityId }) => {
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/ledger_banks/`,
          method: "post",
        };
      },
      invalidatesTags: (_result, _error) => {
        return [{ type: "PlaidBanks", id: "LIST" }, { type: "Subscriptions" }];
      },
    }),

    syncGroupBanks: build.mutation<{ message: string }[], { groupId: string }>({
      query: ({ groupId }) => {
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/bank_transactions/`,
          method: "post",
        };
      },
      invalidatesTags: (_result, _error) => {
        return [{ type: "PlaidBanks", id: "LIST" }];
      },
    }),

    deleteBank: build.mutation<
      void,
      {
        groupId: string;
        entityId: string;
        bankId: string;
      }
    >({
      query: ({ groupId, entityId, bankId }) => {
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/item/${bankId}/`,
          method: "DELETE",
        };
      },
      invalidatesTags: (_result, _error) => {
        return [
          { type: "PlaidBanks", id: "LIST" },
          { type: "CONNECTION" },
          { type: "Subscriptions" },
        ];
      },
    }),

    disconnectBank: build.mutation<
      void,
      {
        groupId: string;
        bankId: string;
        entityId: string;
      }
    >({
      query: ({ groupId, bankId, entityId }) => {
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/item/${bankId}/`,
          method: "PUT",
        };
      },
      invalidatesTags: (res) =>
        res ? [{ type: "PlaidBanks" }, { type: "Subscriptions" }] : [],
    }),

    RemovePlaidBank: build.mutation<
      any,
      { groupId: string; entityItemId: string }
    >({
      query: ({ groupId, entityItemId }) => {
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/plaid_transactions/`,
          method: "DELETE",
          body: {
            entity_item_id: entityItemId,
          },
        };
      },
      invalidatesTags: (_result, _error) => {
        return [
          { type: "PlaidBanks", id: "LIST" },
          { type: "CONNECTION" },
          { type: "Subscriptions" },
        ];
      },
    }),

    getEntityItemBankAccounts: build.query<
      Banks[],
      { groupId: string; entityItemId: string; entityId: string }
    >({
      query: ({ groupId, entityItemId, entityId }) => {
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/entity_item/${entityItemId}/bank_accounts/`,
        };
      },
      providesTags: (result) => [
        { type: "PlaidBanks" as "PlaidBanks", id: "LIST" },
        { type: "CONNECTION" },
      ],
    }),

    getBankAccountById: build.query<
      {
        bank_brand: BankBrand;
        account: Account & {
          account_number: string;
          account_type: string;
          opening_balance: number;
          opening_balance_date: string;
        };
        last_successful_transaction_update: string;
        in_error_state: boolean;
      },
      {
        groupId: string;
        entityItemId: string;
        entityId: string;
        entityBankAccountId: string;
      }
    >({
      query: ({ groupId, entityItemId, entityId, entityBankAccountId }) => {
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/entity_item/${entityItemId}/bank_accounts/${entityBankAccountId}`,
        };
      },
      providesTags: (result) => [
        { type: "PlaidBanks" as "PlaidBanks", id: "LIST" },
        { type: "CONNECTION" },
      ],
    }),
    updateBankAccount: build.mutation<
      {
        bank_brand: BankBrand;
        account: Account & {
          account_number: string;
          account_type: string;
          opening_balance: string;
          opening_balance_date: string;
        };
        last_successful_transaction_update: string;
        in_error_state: boolean;
      },
      {
        groupId: string;
        entityItemId: string;
        entityId: string;
        entityBankAccountId: string;
        account_number: string;
        account_type: string;
        opening_balance: string;
        opening_balance_date: string;
      }
    >({
      query: ({
        groupId,
        entityItemId,
        entityId,
        entityBankAccountId,
        account_number,
        account_type,
        opening_balance,
        opening_balance_date,
      }) => {
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/entity_item/${entityItemId}/bank_accounts/${entityBankAccountId}`,
          method: "PATCH",
          body: {
            account_number,
            account_type,
            opening_balance,
            opening_balance_date,
          },
        };
      },
      invalidatesTags: (_result, _error) => {
        return [{ type: "PlaidBanks", id: "LIST" }, { type: "Subscriptions" }];
      },
    }),
    getBankAccountStatements: build.query<
      BankStatement[],
      {
        entityId: string;
        entityBankAccountId: string;
        startDate?: string;
        endDate?: string;
        sortBy?: "DSC" | "ASC";
      }
    >({
      query: ({
        entityId,
        entityBankAccountId,
        startDate,
        endDate,
        sortBy,
      }) => {
        const queries = {
          start_date: startDate || null,
          end_date: endDate || null,
          sort_order: sortBy,
        };
        let queryUrl = qs.stringify(queries, {
          skipNulls: true,
        });

        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/account/${entityBankAccountId}/statements/`,
          params: qs.parse(queryUrl),
        };
      },
      providesTags: (result) => (result ? ["BANK_ACCOUNT_STATEMENTS"] : []),
    }),

    refreshAccountStatements: build.mutation<
      { message: string; code: string },
      {
        entityId: string;
        entityBankAccountId: string;
        startDate?: string;
        endDate?: string;
      }
    >({
      query: ({ entityId, entityBankAccountId, startDate, endDate }) => {
        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/account/${entityBankAccountId}/statements/`,
          method: "POST",
          body: { start_date: startDate, end_date: endDate },
        };
      },
      invalidatesTags: (result) => (result ? ["BANK_ACCOUNT_STATEMENTS"] : []),
    }),
    getBankBalances: build.query<
      BankBalances[],
      {
        entityId: string;
        date: string;
      }
    >({
      query: ({ entityId, date }) => {
        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/bank-balances-on-date/`,
          params: {
            date,
          },
        };
      },
      providesTags: (result) => (result ? ["BANK_ACCOUNT_BALANCES"] : []),
    }),
    updateBankAccountantAccess: build.mutation<
      any,
      {
        entityId: string;
        entityItemId: string;
        accountantAccess: boolean;
        reason?: string;
      }
    >({
      query: ({ entityItemId, entityId, accountantAccess, reason }) => {
        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/entity_item/${entityItemId}/`,
          method: "PUT",
          body: {
            accountant_access: accountantAccess,
            ...(reason && { reason: reason }),
          },
        };
      },
      invalidatesTags: (_result, _error, arg) => [
        "BOOKKEEPING_ENTITY",
        { type: "PlaidBanks", id: `ENTITY_${arg.entityId}` },
      ],
    }),

    requestAccountantAccess: build.mutation<
      { message_card_id: string },
      {
        entityId: string;
        entityItemIds: string[];
        channelId: string;
        customBankNames?: string[];
      }
    >({
      query: ({ entityId, entityItemIds, channelId, customBankNames }) => ({
        url: `/api/inkle/bookkeeping/entity/${entityId}/accountant_access_request/`,
        method: "POST",
        body: {
          entity_item_ids: entityItemIds,
          channel_id: channelId,
          custom_bank_names: customBankNames ?? [],
        },
      }),
    }),

    respondToAccountantAccessRequest: build.mutation<
      { status: "partial" | "submitted" },
      {
        entityId: string;
        messageCardId: string;
        entityItemResponses: {
          entity_item_id: string;
          accountant_access: boolean;
          reason: string;
        }[];
      }
    >({
      query: ({ entityId, messageCardId, entityItemResponses }) => ({
        url: `/api/inkle/bookkeeping/entity/${entityId}/accountant_access_request/${messageCardId}/respond/`,
        method: "PUT",
        body: { entity_item_responses: entityItemResponses },
      }),
      invalidatesTags: (_result, _error, arg) => [
        "BOOKKEEPING_ENTITY",
        { type: "PlaidBanks", id: `ENTITY_${arg.entityId}` },
      ],
    }),

    getIntermediateBanks: build.query<
      {
        accounts: {
          name: string;
          mask: string;
        }[];
        plaid_item_id: string;
        institution_id: string;
      },
      {
        entityId: string;
        code?: string;
        state?: string;
        entityItemId?: string;
        mapping_type: "DIRECT" | "PLAID";
        connection_id?: string;
        public_token?: string;
      }
    >({
      query: ({
        entityId,
        code,
        public_token,
        entityItemId,
        mapping_type,
        state,
        connection_id,
      }) => {
        return {
          url: `api/inkle/bookkeeping/entity/${entityId}/account_mapper/${entityItemId}/`,
          params: { code, public_token, mapping_type, state, connection_id },
        };
      },
    }),

    getNewIntermediateBanks: build.query<
      {
        accounts: {
          name: string;
          mask: string;
          balance: number;
          current_balance: number;
          currency?: string;
        }[];
        plaid_item_id: string;
        institution_id: string;
      },
      {
        entityId: string;
        code?: string;
        mapping_type: "DIRECT" | "PLAID";
        connection_id?: string;
        public_token?: string;
      }
    >({
      query: ({
        entityId,
        code,
        public_token,
        mapping_type,
        connection_id,
      }) => {
        return {
          url: `api/inkle/bookkeeping/entity/${entityId}/account_mappers/`,
          params: { code, public_token, mapping_type, connection_id },
        };
      },
    }),

    finishMigration: build.mutation<
      void,
      {
        entityId: string;
        entityItemId?: string;
        payload: {
          code: string;
          txn_pull_choice?: string;
          txn_pull_date?: string;
          account_mappers: AccountMigrateMap[];
          new_plaid_item_id?: string;
          institution_id?: string;
          mapping_type?: "DIRECT" | "PLAID";
          connection_id?: string;
        };
      }
    >({
      query: ({ entityId, payload, entityItemId }) => {
        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/account_mapper/${entityItemId}/`,
          body: payload,
          method: "POST",
        };
      },
      invalidatesTags: (result, _error) =>
        result ? [{ type: "PlaidBanks", id: "LIST" }] : [],
    }),

    confirmAccounts: build.mutation<
      void,
      {
        entityId: string;
        payload: {
          account_mappers?: { mask: string; name: string; currency?: string }[];
          new_plaid_item_id?: string;
          institution_id?: string;
          mapping_type?: "DIRECT" | "PLAID";
          connection_id?: string;
          entity_item_id?: string;
          public_token?: string;
          code?: string;
        };
      }
    >({
      query: ({ entityId, payload }) => {
        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/account_mappers/`,
          body: payload,
          method: "POST",
        };
      },
      invalidatesTags: (_result, _error) => {
        return [{ type: "PlaidBanks", id: "LIST" }, { type: "CONNECTION" }];
      },
    }),

    markAccountAsReviewed: build.mutation<
      void,
      {
        entityId: string;
        entityItemId: string;
        groupId: string;
      }
    >({
      query: ({ entityId, entityItemId, groupId }) => ({
        url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/entity_item/${entityItemId}/mark_as_reviewed/`,
        method: "PATCH",
      }),
      invalidatesTags: (result, _error) =>
        result
          ? [{ type: "PlaidBanks", id: "LIST" }, { type: "CONNECTION" }]
          : [],
    }),

    updateDirectAccounts: build.mutation<
      void,
      {
        entityId: string;
        entityItemId: string;
        payload: { mask: string; name: string }[];
      }
    >({
      query: ({ entityId, entityItemId, payload }) => ({
        url: `/api/inkle/bookkeeping/entity/${entityId}/account_mapper/${entityItemId}/`,
        method: "PUT",
        body: {
          accounts: payload,
        },
      }),
      invalidatesTags: (_result, _error) => [
        { type: "PlaidBanks", id: "LIST" },
        { type: "CONNECTION" },
      ],
    }),

    getIntermediateDirectAccounts: build.query<
      {
        name: string;
        mask: string;
        balance: number;
        account_id: string;
      }[],
      { entityItemId: string; entityId: string }
    >({
      query: ({ entityItemId, entityId }) =>
        `/api/inkle/bookkeeping/entity/${entityId}/direct_accounts/${entityItemId}/`,
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetGenerateLinkTokenQuery,
  useLazyGetGenerateLinkTokenQuery,
  useExchangeTokenMutation,
  useGetPlaidBanksQuery,
  useSyncPlaidTransactionsMutation,
  useGetEntityBanksQuery,
  useSyncEntityTransactionsMutation,
  useGetPlaidBankLogoQuery,
  useLazyGetPlaidBankLogoQuery,
  useSyncGroupBanksMutation,
  useUpdateItemErrorStateMutation,
  useRemovePlaidBankMutation,
  useLazyGetEntityBanksQuery,
  useGetEntityItemBankAccountsQuery,
  useGetBankAccountByIdQuery,
  useUpdateBankAccountMutation,
  useEditBankAccountMutation,
  useDisconnectBankMutation,
  useDeleteBankMutation,
  usePrefetch: useBankConnectionsPrefetch,
  useGetBankAccountStatementsQuery,
  useRefreshAccountStatementsMutation,
  useGetBankBalancesQuery,
  useUpdateBankAccountantAccessMutation,
  useRequestAccountantAccessMutation,
  useRespondToAccountantAccessRequestMutation,
  useLazyGetIntermediateBanksQuery,
  useFinishMigrationMutation,
  useConfirmAccountsMutation,
  useGetIntermediateBanksQuery,
  useGetNewIntermediateBanksQuery,
  useGetIntermediateDirectAccountsQuery,
  useUpdateDirectAccountsMutation,
  useMarkAccountAsReviewedMutation,
} = bankConnectionsApi;
